import { useTranslation } from 'react-i18next';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import {
  DatePicker,
  DateTimePicker,
  PickersInputComponentLocaleText,
  PickersInputLocaleText,
  renderDigitalClockTimeView,
} from '@mui/x-date-pickers';
import moment from 'moment';
import OutsideAlerter from 'shared/components/outside-alerter/outside-alerter';

import { FilterChipDateTimeProps, useFilterChipDateTime } from './hooks';
import styles from './filter-chip-date-time.module.scss';

const FilterChipDateTime = ({
  placeHolder,
  currentDateFilters,
  onDateFiltersChanged,
  includeTime,
  onlyHistory,
  selectedDateColor,
}: Readonly<FilterChipDateTimeProps>): React.JSX.Element => {
  const { t } = useTranslation('filterChip');

  const dateTimePickerLocaleText: PickersInputComponentLocaleText = {
    fieldYearPlaceholder: () => t('dateTimePickerLocale.fieldYearPlaceholder'),
    fieldDayPlaceholder: () => t('dateTimePickerLocale.fieldDayPlaceholder'),
    fieldHoursPlaceholder: () => t('dateTimePickerLocale.fieldHoursPlaceholder'),
    fieldMinutesPlaceholder: () => t('dateTimePickerLocale.fieldMinutesPlaceholder'),
    fieldWeekDayPlaceholder: () => t('dateTimePickerLocale.fieldWeekDayPlaceholder'),
    toolbarTitle: t('dateTimePickerLocale.toolbarTitle'),
    cancelButtonLabel: t('dateTimePickerLocale.cancelButton'),
    clearButtonLabel: t('dateTimePickerLocale.clearButton'),
    okButtonLabel: t('dateTimePickerLocale.okButton'),
  };

  const datePickerLocaleText: PickersInputLocaleText & PickersInputComponentLocaleText = {
    ...dateTimePickerLocaleText,
    toolbarTitle: t('datePickerLocale.toolbarTitle'),
  };

  const customToolbarStyle = {
    '& .MuiTypography-root': {
      fontSize: 16,
    },
    '& .MuiButtonBase-root': {
      background: 'white',
    },
    '& .MuiButtonBase-root:active': {
      background: 'white',
    },
    '& .MuiButtonBase-root:hover': {
      background: 'white',
    },
    '& .MuiTouchRipple-root': {
      display: 'none',
    },
  };

  const toolbarStyle = {
    ...customToolbarStyle,
    '& .MuiDatePickerToolbar-title': {
      color: selectedDateColor,
      fontWeight: 700,
    },
  };

  const selectedDayStyle = {
    sx: {
      '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus, &.Mui-selected.Mui-focusVisible': {
        backgroundColor: 'primary.main',
        color: 'primary.contrastText',
      },
    },
  };

  const {
    isOpen,
    toggleIsOpen,
    setDatePickerIsOpen,
    dateFilters,
    setDateFilters,
    isAnyDateFilterFilled,
    handleCancelClick,
    handleApplyClick,
    handleOutsideAlerter,
  } = useFilterChipDateTime({
    placeHolder,
    currentDateFilters,
    onDateFiltersChanged,
    includeTime,
    onlyHistory,
    selectedDateColor,
  });

  return (
    <OutsideAlerter funcDelegate={handleOutsideAlerter}>
      <div className={styles.filterChipDate}>
        <Chip
          data-testid={`filterChip${placeHolder}`}
          label={placeHolder}
          variant="outlined"
          clickable
          deleteIcon={<ArrowDropDownIcon />}
          onDelete={toggleIsOpen}
          onClick={toggleIsOpen}
          className={`${isAnyDateFilterFilled() ? styles.active : ''}`}
        />
        <Paper elevation={1} className={`${styles.options} ${isOpen ? '' : styles.hidden}`}>
          <div className={styles.dateTimePickerRow}>
            {includeTime ? (
              <>
                <DateTimePicker
                  ampm={false}
                  maxDateTime={dateFilters.dateTo ?? (onlyHistory ? moment() : undefined)}
                  label={t('from')}
                  closeOnSelect={false}
                  format="DD-MM-YYYY HH:mm"
                  localeText={dateTimePickerLocaleText}
                  value={dateFilters.dateFrom}
                  onOpen={() => setDatePickerIsOpen(true)}
                  onClose={() => setDatePickerIsOpen(false)}
                  onChange={(value) =>
                    setDateFilters({
                      ...dateFilters,
                      dateFrom: value,
                    })
                  }
                  views={['hours', 'day']}
                  viewRenderers={{
                    hours: renderDigitalClockTimeView,
                  }}
                  slotProps={{
                    toolbar: {
                      toolbarFormat: 'dddd DD MMMM',
                      hidden: false,
                      sx: toolbarStyle,
                    },
                    actionBar: {
                      actions: ['cancel', 'clear', 'accept'],
                    },
                    popper: {
                      disablePortal: true,
                    },
                    day: selectedDayStyle,
                  }}
                />
                <DateTimePicker
                  ampm={false}
                  minDateTime={dateFilters.dateFrom ?? undefined}
                  maxDateTime={onlyHistory ? moment() : undefined}
                  label={t('to')}
                  closeOnSelect={false}
                  format="DD-MM-YYYY HH:mm"
                  localeText={dateTimePickerLocaleText}
                  value={dateFilters.dateTo}
                  onOpen={() => setDatePickerIsOpen(true)}
                  onClose={() => setDatePickerIsOpen(false)}
                  onChange={(value) =>
                    setDateFilters({
                      ...dateFilters,
                      dateTo: value,
                    })
                  }
                  views={['hours', 'day']}
                  viewRenderers={{
                    hours: renderDigitalClockTimeView,
                  }}
                  slotProps={{
                    toolbar: {
                      toolbarFormat: 'dddd DD MMMM',
                      hidden: false,
                      sx: toolbarStyle,
                    },
                    actionBar: {
                      actions: ['cancel', 'clear', 'accept'],
                    },
                    popper: {
                      disablePortal: true,
                    },
                    day: selectedDayStyle,
                  }}
                />
              </>
            ) : (
              <>
                <DatePicker
                  maxDate={dateFilters.dateTo ?? (onlyHistory ? moment() : undefined)}
                  label={t('from')}
                  closeOnSelect={false}
                  sx={{ mb: 2 }}
                  format="DD-MM-YYYY"
                  localeText={datePickerLocaleText}
                  value={dateFilters.dateFrom}
                  onOpen={() => setDatePickerIsOpen(true)}
                  onClose={() => setDatePickerIsOpen(false)}
                  onChange={(value) =>
                    setDateFilters({
                      ...dateFilters,
                      dateFrom: value,
                    })
                  }
                  slotProps={{
                    toolbar: {
                      toolbarFormat: 'dddd DD MMMM',
                      hidden: false,
                      sx: toolbarStyle,
                    },
                    actionBar: {
                      actions: ['cancel', 'clear', 'accept'],
                    },
                    popper: {
                      disablePortal: true,
                    },
                    day: selectedDayStyle,
                  }}
                />
                <DatePicker
                  minDate={dateFilters.dateFrom ?? undefined}
                  maxDate={onlyHistory ? moment() : undefined}
                  label={t('to')}
                  closeOnSelect={false}
                  format="DD-MM-YYYY"
                  localeText={datePickerLocaleText}
                  value={dateFilters.dateTo}
                  onOpen={() => setDatePickerIsOpen(true)}
                  onClose={() => setDatePickerIsOpen(false)}
                  onChange={(value) =>
                    setDateFilters({
                      ...dateFilters,
                      dateTo: value,
                    })
                  }
                  slotProps={{
                    toolbar: {
                      toolbarFormat: 'dddd DD MMMM',
                      hidden: false,
                      sx: toolbarStyle,
                    },
                    actionBar: {
                      actions: ['cancel', 'clear', 'accept'],
                    },
                    popper: {
                      disablePortal: true,
                    },
                    day: selectedDayStyle,
                  }}
                />
              </>
            )}
          </div>

          <div className={styles.buttonsRow}>
            <Button variant="outlined" onClick={handleCancelClick}>
              {t('cancel')}
            </Button>
            <Button variant="contained" onClick={handleApplyClick}>
              {t('apply')}
            </Button>
          </div>
        </Paper>
      </div>
    </OutsideAlerter>
  );
};

export default FilterChipDateTime;
